import { cancelOnSessionChange } from './session-cancellation';
// src/app/state/effects/auth.effects.ts

import { Inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { tap, switchMap, map, catchError, mergeMap, ignoreElements } from 'rxjs/operators';
import { of, from } from 'rxjs';
import * as AuthActions from '../actions/auth.actions';
import * as OrganizationsActions from '../actions/organizations/core.actions';
import { MessageService } from 'primeng/api';
import {
  Auth,
  GithubAuthProvider,
} from '@angular/fire/auth';
import { CookieService } from 'ngx-cookie-service';
import { AuthService as ApiAuthService } from '@api/services/auth.service';
import { AUTH_EFFECT_OPERATIONS, FirebaseAuthOperations, firebaseAuthOperations } from './auth-operations';

@Injectable()
export class AuthEffects {
  authSignInSuccess$;
  logout$;
  signUpWithEmailPassword$;
  signInWithEmailPassword$;
  loginWithGithub$;
  loginAsGuest$;
  authFailure$;
  forgotPassword$;
  forgotPasswordSuccess$;
  sendActivationEmail$;
  loadOrganizationsSuccess$;
  loadUserData$;
  clearUserData$;

  constructor(
    private logger: NGXLogger,
    private actions$: Actions,
    private store: Store,
    private router: Router,
    private auth: Auth,
    private messageService: MessageService,
    private apiAuthService: ApiAuthService,
    private cookieService: CookieService,
    @Inject(AUTH_EFFECT_OPERATIONS) private authOperations: FirebaseAuthOperations = firebaseAuthOperations,
  ) {
    this.authSignInSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.authSignInSuccess),
        mergeMap(() => {
          this.messageService.add({ severity: 'success', summary: 'Logged in', detail: 'You have been logged in.' });
          return of(OrganizationsActions.loadOrganizations());
        })
      )
    );
    this.authFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.authFailure),
        tap(({ error }) => {
          const friendlyMessage = this.getFriendlyErrorMessage(error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: friendlyMessage
          });
        })
      ),
      { dispatch: false }
    );

    this.logout$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        switchMap(() =>
          this.apiAuthService.authSessionLogout().pipe(
            switchMap(() =>
              from(this.authOperations.signOut(this.auth)).pipe(
                tap(() => {
                  this.router.navigate(['auth/sign-in']);
                  this.messageService.add({ severity: 'info', summary: 'Logged out', detail: 'You have been logged out.' });
                }),
                ignoreElements(),
                catchError(err => of(AuthActions.authFailure({ error: err })))
              )
            ),
            catchError(err => of(AuthActions.authFailure({ error: err }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.signUpWithEmailPassword$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.signUpWithEmailPassword),
        switchMap(({ email, password, fullName }) =>
          from(this.apiAuthService.authCreateUserWithEmailAndPassword({ body: { 
              email: email.trim(),
              password,
              fullName: fullName.trim()
            }})).pipe(
            switchMap(credential =>
              from(this.authOperations.signInWithEmailAndPassword(this.auth, email.trim(), password)).pipe(
                map(() => {
                  if (credential.userData?.emailVerified === false) {
                    this.router.navigate(['auth/activate-email']);
                    this.messageService.add({
                      severity: 'info',
                      summary: 'Email Verification Sent',
                      detail: 'Please verify your email address to complete sign up.'
                    });
                    return AuthActions.sendActivationEmail();
                  }
                  return AuthActions.authSignInSuccess({
                    userId: credential.userData?.id || null,
                    customToken: '',
                    userData: credential.userData || null
                  });
                })
              )
            ),
            catchError(err => of(AuthActions.authFailure({ error: err }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.signInWithEmailPassword$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.signInWithEmailPassword),
        switchMap(({ email, password }) =>
          from(this.authOperations.signInWithEmailAndPassword(this.auth, email, password)).pipe(
            switchMap((credential) => {
              if (credential.user?.emailVerified === false) {
                this.router.navigate(['auth/activate-email']);
                this.messageService.add({
                  severity: 'info',
                  summary: 'Email Verification Required',
                  detail: 'Please verify your email address to sign in.',
                  life: 8000
                });
                return of(AuthActions.authFailure({ error: { code: 'auth/email-not-verified' } }));
              }
              return from(credential.user.getIdToken()).pipe(
                switchMap(firebaseIdToken => {
                  if (!firebaseIdToken) {
                    return of(AuthActions.authFailure({ error: { code: 'auth/no-id-token' } }));
                  }
                  return this.apiAuthService.authCsrfToken().pipe(
                    switchMap(() => {
                      const csrfToken = this.cookieService.get('csrfToken');
                      if (!csrfToken) {
                        return of(AuthActions.authFailure({ error: { code: 'auth/no-csrf-token' } }));
                      }
                      return this.apiAuthService.authSessionLogin({ body: { csrfToken, idToken: firebaseIdToken } }).pipe(
                        map((response) => {
                          const userId = response.userData?.id || null;
                          return AuthActions.authSignInSuccess({
                            userId,
                            customToken: null,
                            userData: response.userData || null
                          });
                        }),
                        catchError(err => of(AuthActions.authFailure({ error: err })))
                      );
                    })
                  );
                })
              );
            }),
            catchError(err => of(AuthActions.authFailure({ error: err }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.loginWithGithub$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.loginWithGithub),
        switchMap(() =>
          from(this.authOperations.signInWithPopup(this.auth, new GithubAuthProvider())).pipe(
            map(credential => AuthActions.authSignInSuccess({
              userId: credential.user?.uid || null,
              customToken: ''
            })),
            catchError(err => of(AuthActions.authFailure({ error: err }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.loginAsGuest$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.loginAsGuest),
        switchMap(() =>
          from(this.authOperations.signInAnonymously(this.auth)).pipe(
            map(credential => AuthActions.authSignInSuccess({
              userId: credential.user?.uid || null,
              customToken: ''
            })),
            catchError(err => of(AuthActions.authFailure({ error: err }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.forgotPassword$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.forgotPassword),
        mergeMap(action =>
          from(this.authOperations.sendPasswordResetEmail(this.auth, action.email)).pipe(
            map(() => AuthActions.forgotPasswordSuccess()),
            catchError(error => of(AuthActions.authFailure({ error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.forgotPasswordSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.forgotPasswordSuccess),
        tap(() => {
          this.messageService.add({ severity: 'success', summary: 'Email Sent', detail: 'Password reset email sent.' });
        })
      ),
      { dispatch: false }
    );

    this.sendActivationEmail$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.sendActivationEmail),
        switchMap(() =>
          from(this.authOperations.sendEmailVerification(this.auth.currentUser!)).pipe(
            map(() => AuthActions.sendActivationEmailSuccess()),
            catchError(err => of(AuthActions.authFailure({ error: err }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.loadOrganizationsSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.loadOrganizationsSuccess),
        tap(({ organizations }) => {
          const organizationId = this.cookieService.get('invite_organizationId');
          const token = this.cookieService.get('invite_token');
          const invitationId = this.cookieService.get('invite_invitationId');
          if (organizationId && token && invitationId) {
            this.router.navigate(
              ['/response-invite-landing', invitationId],
              { queryParams: { organizationId, token } }
            );
            return;
          }
          if (!organizations || organizations.length === 0) {
            this.messageService.add({ severity: 'info', summary: 'No organization', detail: 'Please create an organization and a project', life: 8000 });
            this.router.navigate(['organization/new']);
          } else if (organizations.length === 1) {
            this.router.navigate([`organization/${organizations[0].data.id}/overview`]);
          } else {
            this.router.navigate(['organizations']);
          }
        })
      ),
      { dispatch: false }
    );

    this.loadUserData$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.authMe),
        switchMap(({ domain }) =>
          from(this.apiAuthService.authMe({ domain })).pipe(
            map(response => AuthActions.authMeSuccess({
              userData: response.userData || null,
              projectData: response.projectData || null
            })),
            catchError(error => of(AuthActions.authFailure({ error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.clearUserData$ = createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActions.clearUserData),
        tap(() => {
          this.router.navigate(['auth/sign-in']);
          this.messageService.add({ severity: 'info', summary: 'Login Required', detail: 'Please log in to continue.', life: 8000 });
        })
      ),
      { dispatch: false }
    );
  }

  private getFriendlyErrorMessage(error: any): string {
    this.logger.error(error);
    switch (error.code) {
      // Sign up errors
      case 'auth/email-already-exists':
      case 'auth/email-already-in-use':
        return 'This email address is already in use.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/invalid-password':
      case 'auth/weak-password':
        return 'Password must be at least 6 characters long.';
      // Sign in errors
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      // Popup (GitHub) errors
      case 'auth/popup-closed-by-user':
        return 'The sign-in popup was closed before completing sign in.';
      case 'auth/cancelled-popup-request':
        return 'The sign-in process was cancelled.';
      // Anonymous sign-in errors
      case 'auth/operation-not-allowed':
        return 'Anonymous sign-in is not enabled.';
      case 'auth/invalid-credential':
        return 'The credentials are invalid. Please check your input.';
      case 'auth/email-not-verified':
        return 'Please verify your email address to sign in.';
      case 'auth/session-cookie-revoked':
        return 'Session cookie has been revoked. Please sign in again.';
      case 'auth/session-cookie-expired':
        return 'Session cookie has expired. Please sign in again.';
      default:
        return 'An unexpected error occurred. Please try again later.';
    }
  }
}
